import React, { useState, useEffect } from 'react';
import {
  StyleSheet,
  Text,
  View,
  ScrollView,
  TouchableOpacity,
  Linking,
  Image,
  Dimensions,
  TextInput,
  Alert,
  Modal,
  KeyboardAvoidingView,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { LinearGradient } from 'expo-linear-gradient';
import { MaterialIcons, FontAwesome5, AntDesign } from '@expo/vector-icons';

export default function App() {
  const [dimensions, setDimensions] = useState(Dimensions.get('window'));
  const [showEnquiryForm, setShowEnquiryForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });

  // Configure your PHP endpoint URL here
  // For local testing: 'http://localhost/send_enquiry.php'
  // For production: 'https://creativewebie.org/send_enquiry.php'
  const API_ENDPOINT = 'https://creativewebie.org/send_enquiry.php';

  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setDimensions(window);
    });
    return () => subscription?.remove();
  }, []);

  const isLargeScreen = dimensions.width >= 768;
  const isTablet = dimensions.width >= 600;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      Alert.alert('Validation Error', 'Please enter your name');
      return false;
    }
    if (!formData.email.trim()) {
      Alert.alert('Validation Error', 'Please enter your email');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      Alert.alert('Validation Error', 'Please enter a valid email address');
      return false;
    }
    if (!formData.phone.trim()) {
      Alert.alert('Validation Error', 'Please enter your phone number');
      return false;
    }
    if (!formData.service.trim()) {
      Alert.alert('Validation Error', 'Please select a service');
      return false;
    }
    if (!formData.message.trim()) {
      Alert.alert('Validation Error', 'Please enter your message');
      return false;
    }
    return true;
  };

  const handleSubmitEnquiry = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare form data
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('service', formData.service);
      formDataToSend.append('message', formData.message);

      // Send POST request to PHP backend
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
        },
        body: formDataToSend,
      });

      const result = await response.json();

      setIsSubmitting(false);

      if (result.success) {
        // Success - show success message
        Alert.alert(
          'Enquiry Submitted Successfully!',
          result.message || `Thank you ${formData.name}! Your enquiry has been submitted successfully. We will get back to you soon.`,
          [
            {
              text: 'OK',
              onPress: () => {
                // Reset form and close modal
                setFormData({
                  name: '',
                  email: '',
                  phone: '',
                  service: '',
                  message: '',
                });
                setShowEnquiryForm(false);
              },
            },
          ]
        );
      } else {
        // Error - show error message
        Alert.alert(
          'Submission Failed',
          result.message || 'Sorry, there was an error submitting your enquiry. Please try again later or contact us directly.',
          [{ text: 'OK' }]
        );
      }
    } catch (error) {
      setIsSubmitting(false);
      console.error('Error submitting enquiry:', error);
      Alert.alert(
        'Network Error',
        'Unable to connect to the server. Please check your internet connection and try again, or contact us directly at info@creativewebie.org',
        [{ text: 'OK' }]
      );
    }
  };
  const openWebsite = () => {
    Linking.openURL('https://creativewebie.org');
  };

  const openEmail = () => {
    Linking.openURL('mailto:anurag@creativewebie.org');
  };

  const openPhone = () => {
    Linking.openURL('tel:+919619948341');
  };

  const services = [
    {
      icon: 'web',
      title: 'Website Development',
      description: 'Custom websites tailored to your business needs',
      color: '#2563eb',
    },
    {
      icon: 'code',
      title: 'Web Applications',
      description: 'Scalable and robust web applications',
      color: '#0ea5e9',
    },
    {
      icon: 'computer',
      title: 'Software Services',
      description: 'End-to-end software solutions',
      color: '#8b5cf6',
    },
  ];

  return (
    <View style={styles.container}>
      <StatusBar style="light" />
      <ScrollView showsVerticalScrollIndicator={false}>
        {/* Header Section */}
        <LinearGradient
          colors={['#FFFDFD', '#000000']}
          style={styles.header}
        >
          <View style={styles.headerContent}>
            <View style={styles.logoContainer}>
              <Image 
                source={require('./logocolor.png')} 
                style={styles.logoImage}
                resizeMode="contain"
              />
            </View>
            <Text style={styles.companyName}>CreativeWebie</Text>
            <Text style={styles.tagline}>Building Digital Excellence</Text>
            <Text style={styles.location}>
              <MaterialIcons name="location-on" size={16} color="#555" /> Mumbai, India
            </Text>
          </View>
        </LinearGradient>

        {/* Content Sections Container - Responsive Layout */}
        <View style={[styles.contentContainer, isLargeScreen && styles.contentContainerRow]}>
          {/* About Section */}
          <View style={[styles.section, isLargeScreen && styles.sectionFlex]}>
            <Text style={styles.sectionTitle}>About Us</Text>
            <Text style={styles.sectionText}>
              CreativeWebie is a leading web development and software services company 
              based in Mumbai, India. We specialize in creating stunning websites, 
              powerful web applications, and comprehensive software solutions that 
              help businesses thrive in the digital world.
            </Text>
          </View>

          {/* Services Section */}
          <View style={[styles.section, isLargeScreen && styles.sectionFlex]}>
            <Text style={styles.sectionTitle}>Our Services</Text>
            <View style={[styles.servicesContainer, isTablet && styles.servicesRow]}>
              {services.map((service, index) => (
                <View key={index} style={[styles.serviceCard, isTablet && styles.serviceCardFlex]}>
                  <View style={[
                    styles.serviceIconContainer, 
                    { backgroundColor: service.color + '20' },
                    isTablet && styles.serviceIconContainerColumn
                  ]}>
                    <MaterialIcons name={service.icon} size={32} color={service.color} />
                  </View>
                  <View style={styles.serviceContent}>
                    <Text style={styles.serviceTitle}>{service.title}</Text>
                    <Text style={styles.serviceDescription}>{service.description}</Text>
                  </View>
                </View>
              ))}
            </View>
          </View>
        </View>

        {/* Contact Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Get In Touch</Text>
          <View style={[styles.contactGrid, isTablet && styles.contactGridRow]}>
          
            <TouchableOpacity style={[styles.contactCard, isTablet && styles.contactCardFlex]} onPress={openWebsite}>
              <View style={styles.contactIconContainer}>
                <MaterialIcons name="language" size={24} color="#2563eb" />
              </View>
              <View style={styles.contactContent}>
                <Text style={styles.contactLabel}>Website</Text>
                <Text style={styles.contactValue}>creativewebie.org</Text>
              </View>
              <MaterialIcons name="chevron-right" size={24} color="#999" />
            </TouchableOpacity>

            <TouchableOpacity style={[styles.contactCard, isTablet && styles.contactCardFlex]} onPress={openEmail}>
              <View style={styles.contactIconContainer}>
                <MaterialIcons name="email" size={24} color="#2563eb" />
              </View>
              <View style={styles.contactContent}>
                <Text style={styles.contactLabel}>Email</Text>
                <Text style={styles.contactValue}>anurag@creativewebie.org</Text>
              </View>
              <MaterialIcons name="chevron-right" size={24} color="#999" />
            </TouchableOpacity>

            <TouchableOpacity style={[styles.contactCard, isTablet && styles.contactCardFlex]} onPress={openPhone}>
              <View style={styles.contactIconContainer}>
                <MaterialIcons name="phone" size={24} color="#2563eb" />
              </View>
              <View style={styles.contactContent}>
                <Text style={styles.contactLabel}>Phone</Text>
                <Text style={styles.contactValue}>+91 9619948341</Text>
              </View>
              <MaterialIcons name="chevron-right" size={24} color="#999" />
            </TouchableOpacity>

            <TouchableOpacity style={[styles.contactCard, isTablet && styles.contactCardFlex]}>
              <View style={styles.contactIconContainer}>
                <MaterialIcons name="location-on" size={24} color="#2563eb" />
              </View>
              <View style={styles.contactContent}>
                <Text style={styles.contactLabel}>Location</Text>
                <Text style={styles.contactValue}>Mumbai, Maharashtra, India</Text>
              </View>
            </TouchableOpacity>
          </View>
        </View>

        {/* CTA Section */}
        <View style={[styles.ctaSection, isTablet && styles.ctaSectionRow]}>
          <TouchableOpacity 
            style={[styles.ctaButton, styles.enquiryButton, isTablet && styles.ctaButtonFlex]} 
            onPress={() => setShowEnquiryForm(true)}
            activeOpacity={0.8}
          >
            <LinearGradient
              colors={['#EDB132', '#EE7129']}
              style={styles.ctaButtonGradient}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
            >
              <MaterialIcons name="send" size={20} color="#fff" />
              <Text style={styles.ctaButtonText}>Raise an Enquiry</Text>
            </LinearGradient>
          </TouchableOpacity>
          <TouchableOpacity 
            style={[styles.ctaButton, isTablet && styles.ctaButtonFlex]} 
            onPress={openWebsite}
            activeOpacity={0.8}
          >
            <LinearGradient
              colors={['#EDB132', '#EE7129']}
              style={styles.ctaButtonGradient}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
            >
              <Text style={styles.ctaButtonText}>Visit Our Website</Text>
              <MaterialIcons name="arrow-forward" size={20} color="#fff" />
            </LinearGradient>
          </TouchableOpacity>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>© 2025 CreativeWebie. All rights reserved.</Text>
          <Text style={styles.footerSubtext}>Mumbai, India</Text>
        </View>
      </ScrollView>

      {/* Enquiry Form Modal */}
      <Modal
        visible={showEnquiryForm}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setShowEnquiryForm(false)}
      >
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={styles.modalOverlay}
        >
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Raise an Enquiry</Text>
              <TouchableOpacity
                onPress={() => setShowEnquiryForm(false)}
                style={styles.closeButton}
              >
                <MaterialIcons name="close" size={24} color="#666" />
              </TouchableOpacity>
            </View>

            <ScrollView showsVerticalScrollIndicator={false} style={styles.formScroll}>
              <View style={styles.formGroup}>
                <Text style={styles.label}>Name *</Text>
                <TextInput
                  style={styles.input}
                  placeholder="Enter your name"
                  value={formData.name}
                  onChangeText={(value) => handleInputChange('name', value)}
                  placeholderTextColor="#999"
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.label}>Email *</Text>
                <TextInput
                  style={styles.input}
                  placeholder="Enter your email"
                  value={formData.email}
                  onChangeText={(value) => handleInputChange('email', value)}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  placeholderTextColor="#999"
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.label}>Phone *</Text>
                <TextInput
                  style={styles.input}
                  placeholder="Enter your phone number"
                  value={formData.phone}
                  onChangeText={(value) => handleInputChange('phone', value)}
                  keyboardType="phone-pad"
                  placeholderTextColor="#999"
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.label}>Service Interested In *</Text>
                <View style={styles.serviceOptions}>
                  {services.map((service, index) => (
                    <TouchableOpacity
                      key={index}
                      style={[
                        styles.serviceOption,
                        formData.service === service.title && styles.serviceOptionSelected,
                      ]}
                      onPress={() => handleInputChange('service', service.title)}
                    >
                      <MaterialIcons
                        name={service.icon}
                        size={20}
                        color={formData.service === service.title ? '#fff' : service.color}
                      />
                      <Text
                        style={[
                          styles.serviceOptionText,
                          formData.service === service.title && styles.serviceOptionTextSelected,
                        ]}
                      >
                        {service.title}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.label}>Message *</Text>
                <TextInput
                  style={[styles.input, styles.textArea]}
                  placeholder="Tell us about your project requirements..."
                  value={formData.message}
                  onChangeText={(value) => handleInputChange('message', value)}
                  multiline
                  numberOfLines={5}
                  textAlignVertical="top"
                  placeholderTextColor="#999"
                />
              </View>

              <TouchableOpacity
                style={[styles.submitButton, isSubmitting && styles.submitButtonDisabled]}
                onPress={handleSubmitEnquiry}
                disabled={isSubmitting}
                activeOpacity={0.8}
              >
                <LinearGradient
                  colors={['#EDB132', '#EE7129']}
                  style={styles.submitButtonGradient}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 0 }}
                >
                  {isSubmitting ? (
                    <>
                      <ActivityIndicator size="small" color="#fff" />
                      <Text style={styles.submitButtonText}>Submitting...</Text>
                    </>
                  ) : (
                    <>
                      <MaterialIcons name="send" size={20} color="#fff" />
                      <Text style={styles.submitButtonText}>Submit Enquiry</Text>
                    </>
                  )}
                </LinearGradient>
              </TouchableOpacity>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  header: {
    paddingTop: 60,
    paddingBottom: 40,
    paddingHorizontal: 20,
    borderBottomLeftRadius: 30,
    borderBottomRightRadius: 30,
  },
  headerContent: {
    alignItems: 'center',
  },
  logoContainer: {
    width: 120,
    height: 120,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
  },
  logoImage: {
    width: '100%',
    height: '100%',
  },
  companyName: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#fff',
    marginBottom: 8,
    textShadowColor: 'rgba(255, 255, 255, 0.8)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 3,
  },
  tagline: {
    fontSize: 16,
    color: '#fff',
    opacity: 0.9,
    marginBottom: 12,
  },
  location: {
    fontSize: 14,
    color: '#fff',
    opacity: 0.8,
  },
  contentContainer: {
    flexDirection: 'column',
  },
  contentContainerRow: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    gap: 20,
  },
  section: {
    padding: 20,
    backgroundColor: '#fff',
    marginTop: 20,
    marginHorizontal: 20,
    borderRadius: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  sectionFlex: {
    flex: 1,
    marginHorizontal: 0,
  },
  sectionTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 15,
  },
  sectionText: {
    fontSize: 16,
    color: '#666',
    lineHeight: 24,
  },
  servicesContainer: {
    flexDirection: 'column',
  },
  servicesRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 15,
  },
  serviceCard: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
    padding: 15,
    backgroundColor: '#f9f9f9',
    borderRadius: 12,
  },
  serviceCardFlex: {
    flex: 1,
    minWidth: 200,
    marginBottom: 0,
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  serviceIconContainer: {
    width: 60,
    height: 60,
    borderRadius: 30,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 15,
  },
  serviceIconContainerColumn: {
    marginRight: 0,
    marginBottom: 15,
  },
  serviceCardFlex: {
    flex: 1,
    minWidth: 200,
    marginBottom: 0,
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  serviceContent: {
    flex: 1,
  },
  serviceTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 5,
  },
  serviceDescription: {
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
  },
  contactGrid: {
    flexDirection: 'column',
  },
  contactGridRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  contactCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 15,
    backgroundColor: '#f9f9f9',
    borderRadius: 12,
    marginBottom: 12,
  },
  contactCardFlex: {
    flex: 1,
    minWidth: 200,
    marginBottom: 0,
  },
  contactIconContainer: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: '#f0f0f0',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 15,
  },
  contactContent: {
    flex: 1,
  },
  contactLabel: {
    fontSize: 12,
    color: '#999',
    marginBottom: 4,
  },
  contactValue: {
    fontSize: 16,
    color: '#333',
    fontWeight: '500',
  },
  ctaSection: {
    padding: 20,
    marginTop: 20,
    flexDirection: 'column',
  },
  ctaSectionRow: {
    flexDirection: 'row',
    gap: 12,
  },
  ctaButton: {
    borderRadius: 12,
    marginBottom: 12,
    shadowColor: '#EE7129',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 5,
    overflow: 'hidden',
  },
  ctaButtonGradient: {
    paddingVertical: 16,
    paddingHorizontal: 30,
    borderRadius: 12,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  ctaButtonFlex: {
    flex: 1,
    marginBottom: 0,
  },
  enquiryButton: {
    // Gradient is applied via LinearGradient component
  },
  ctaButtonText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold',
    marginLeft: 10,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 25,
    borderTopRightRadius: 25,
    maxHeight: '90%',
    paddingBottom: 20,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
  },
  closeButton: {
    padding: 5,
  },
  formScroll: {
    padding: 20,
  },
  formGroup: {
    marginBottom: 20,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 10,
    padding: 15,
    fontSize: 16,
    backgroundColor: '#f9f9f9',
    color: '#333',
  },
  textArea: {
    height: 120,
    paddingTop: 15,
  },
  serviceOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  serviceOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 25,
    borderWidth: 2,
    borderColor: '#ddd',
    backgroundColor: '#f9f9f9',
    marginRight: 10,
    marginBottom: 10,
  },
  serviceOptionSelected: {
    backgroundColor: '#2563eb',
    borderColor: '#2563eb',
  },
  serviceOptionText: {
    marginLeft: 8,
    fontSize: 14,
    color: '#333',
    fontWeight: '500',
  },
  serviceOptionTextSelected: {
    color: '#fff',
  },
  submitButton: {
    borderRadius: 12,
    marginTop: 10,
    shadowColor: '#EE7129',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 5,
    overflow: 'hidden',
  },
  submitButtonGradient: {
    paddingVertical: 16,
    paddingHorizontal: 30,
    borderRadius: 12,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  submitButtonDisabled: {
    opacity: 0.6,
  },
  submitButtonText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold',
    marginLeft: 10,
  },
  footer: {
    padding: 30,
    alignItems: 'center',
  },
  footerText: {
    fontSize: 14,
    color: '#999',
    marginBottom: 5,
  },
  footerSubtext: {
    fontSize: 12,
    color: '#bbb',
  },
});

